import React, { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Shield, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

type Dispute = {
  id: number;
  orderId: number;
  assignmentId: number;
  driverId: number;
  failedCodes: string[];
  correctCode: string;
  status: "pending" | "approved" | "declined";
  adminId: number | null;
  adminNote: string | null;
  earningsPence: number;
  bonusPence: number;
  driverName: string | null;
  storeName: string | null;
  createdAt: string;
  resolvedAt: string | null;
  deliveryPhotoUrl: string | null;
};

const STATUS_CFG: Record<string, { label: string; cls: string; icon: React.ReactElement }> = {
  pending:  { label: "Pending Review", cls: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 border-amber-300",  icon: <Clock size={13} /> },
  approved: { label: "Approved",       cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: <CheckCircle size={13} /> },
  declined: { label: "Declined",       cls: "bg-red-50 text-red-700 border-red-300",        icon: <XCircle size={13} /> },
};

export default function AdminDeliveryDisputes() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [noteInputs, setNoteInputs] = useState<Record<number, string>>({});
  const [tab, setTab] = useState<"pending" | "resolved">("pending");

  if (!user || user.role !== "admin") return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const r = await fetch("/api/admin/delivery-disputes", { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error("Failed to load");
      const d = await r.json() as { items: Dispute[] };
      setDisputes(d.items);
    } catch {
      toast({ title: "Failed to load disputes", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function resolve(id: number, action: "approved" | "declined") {
    setActioningId(id);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const r = await fetch(`/api/admin/delivery-disputes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action, adminNote: noteInputs[id] ?? "" }),
      });
      if (!r.ok) {
        const d = await r.json() as { error?: string };
        throw new Error(d.error ?? "Failed");
      }
      toast({ title: action === "approved" ? "Payment approved" : "Payment declined" });
      setDisputes((prev) => prev.map((d) =>
        d.id === id ? { ...d, status: action, resolvedAt: new Date().toISOString() } : d
      ));
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setActioningId(null);
    }
  }

  const filtered = disputes.filter((d) =>
    tab === "pending" ? d.status === "pending" : d.status !== "pending"
  );

  return (
    <AdminLayout current="/admin/delivery-disputes">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Delivery Code Disputes</h1>
            <p className="text-sm text-slate-500">
              Review drivers who completed a delivery after 3 failed code attempts.
            </p>
          </div>
          <div className="ml-auto">
            {disputes.filter((d) => d.status === "pending").length > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-bold px-3 py-1.5 rounded-full">
                {disputes.filter((d) => d.status === "pending").length} pending
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-0">
          {(["pending", "resolved"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors capitalize ${
                tab === t ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <Shield size={36} className="text-slate-300" />
            <p className="text-slate-500 font-medium">
              {tab === "pending" ? "No pending disputes" : "No resolved disputes yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((dispute) => {
              const cfg = STATUS_CFG[dispute.status] ?? STATUS_CFG["pending"];
              const totalEarnings = dispute.earningsPence + dispute.bonusPence;
              const failedCodes = Array.isArray(dispute.failedCodes) ? dispute.failedCodes : [];

              return (
                <div
                  key={dispute.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                >
                  {/* Card header */}
                  <div className="flex items-start gap-4 p-5 border-b border-slate-100">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle size={18} className="text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-foreground">Order #{dispute.orderId}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {dispute.storeName ?? `Store`} · {fmtDate(dispute.createdAt)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-foreground text-lg">{pence(totalEarnings)}</p>
                      <p className="text-xs text-slate-400">driver pay</p>
                    </div>
                  </div>

                  {/* Details grid */}
                  <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Driver */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Driver</p>
                      <p className="text-sm font-semibold text-foreground">{dispute.driverName ?? `ID ${dispute.driverId}`}</p>
                      <p className="text-xs text-slate-400">
                        Earnings: {pence(dispute.earningsPence)} + bonus {pence(dispute.bonusPence)}
                      </p>
                    </div>

                    {/* Codes */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Codes Attempted</p>
                      <div className="flex flex-wrap gap-1">
                        {failedCodes.map((code, i) => (
                          <span key={i} className="inline-block px-2 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded font-mono text-sm font-bold">
                            {code}
                          </span>
                        ))}
                        {failedCodes.length === 0 && <span className="text-xs text-slate-400">none recorded</span>}
                      </div>
                    </div>

                    {/* Correct code */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Correct Code</p>
                      <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg font-mono text-xl font-bold tracking-widest">
                        {dispute.correctCode}
                      </span>
                    </div>
                  </div>

                  {/* Proof of delivery — the evidence this decision rests on. */}
                  <div className="px-5 pb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Proof of Delivery</p>
                    {dispute.deliveryPhotoUrl ? (
                      <a href={dispute.deliveryPhotoUrl} target="_blank" rel="noreferrer" className="inline-block">
                        <img
                          src={dispute.deliveryPhotoUrl}
                          alt={`Delivery photo for order #${dispute.orderId}`}
                          loading="lazy"
                          className="max-h-64 rounded-xl border border-slate-200 dark:border-slate-700 object-contain bg-slate-50 dark:bg-slate-800"
                        />
                        <span className="block mt-1 text-xs text-slate-400">Tap to open full size</span>
                      </a>
                    ) : (
                      <p className="text-xs text-slate-400 italic">
                        No photo supplied. The driver may still be out of signal — photos taken offline upload
                        when the driver's phone reconnects, so check back before declining on this alone.
                      </p>
                    )}
                  </div>

                  {/* Admin note (resolved) or approve/decline (pending) */}
                  {dispute.status === "pending" ? (
                    <div className="px-5 pb-5 space-y-3">
                      <textarea
                        placeholder="Admin note (optional)…"
                        value={noteInputs[dispute.id] ?? ""}
                        onChange={(e) => setNoteInputs((prev) => ({ ...prev, [dispute.id]: e.target.value }))}
                        rows={2}
                        className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void resolve(dispute.id, "approved")}
                          disabled={actioningId === dispute.id}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle size={15} />
                          Approve Payment
                        </button>
                        <button
                          onClick={() => void resolve(dispute.id, "declined")}
                          disabled={actioningId === dispute.id}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          <XCircle size={15} />
                          Decline Payment
                        </button>
                      </div>
                    </div>
                  ) : dispute.adminNote ? (
                    <div className="px-5 pb-5">
                      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Admin Note</p>
                      <p className="text-sm text-slate-600">{dispute.adminNote}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
